import { Component, EventEmitter, Output, signal, inject } from '@angular/core';
import { NgbDropdownModule, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ValidationModal } from './components/modals/validation-modal/validation-modal.component';
import { DateFilterComponent, AccountFilterComponent } from "../../_shared/components";
import { AccountFilterData, DateIntervalFilterData, DriverFilterData, WidgetFilterData } from '../../_shared/models';
import { DateInterval } from '../../_shared/enums';
import { TotalFuelLossWidgetComponent } from './components/widgets/total-fuel-loss-widget/total-fuel-loss-widget.component';
import { TotalFillUpsWidgetComponent } from './components/widgets/total-fill-ups-widget/total-fill-ups-widget.component';
import { LossRatioWidgetComponent } from './components/widgets/loss-ratio-widget/loss-ratio-widget.component';
import { FuelConsumptionWidgetComponent } from './components/widgets/fuel-consumption-widget/fuel-consumption-widget.component';
import { getModalFilterHeaders, getSelectedFilterLabel } from '../../_shared/helpers/kpiWidget/selected-filter.helper';
import { FilterService } from '../../_shared/services';
import { isEmptyObject } from '../../_shared/helpers/object.helper';
import { Subject } from 'rxjs';
import { FuelChangesService } from './services/fuel-changes.service';
import { FuelChangesDetails, FuelChangesLocations, FuelChangesRequest, FuelChangesPaginationRequest } from './models/fuel-changes-details.model';
import { CommonModule } from '@angular/common';
import { ErrorHandlerService } from '../../_shared/services/error-handler.service';
import { PaginationComponent } from '../../_shared/components/pagination/pagination.component';
import { CommonMapService } from '../../_shared/services/map/common-map.service';
import { IClusterRenderer } from '../../_shared/interfaces/maps/base-cluster-renderer';
import { IMarkerFactory } from '../../_shared/interfaces/maps/base-marker-factory';
import { BaseMapInfoWindowBuilder } from '../../_shared/interfaces/maps/base-map-info-window-builder';
import { FuelChangesMarkerFactory } from './helpers/maps/fuel-changes-marker-factory.helper';
import { FuelChangesMapInfoWindowBuilder } from './helpers/maps/fuel-changes-map-info-window-builder.helper';
import { FuelChangesClusterRenderer } from './helpers/maps/fuel-changes-cluster-renderer.helper';
import { FuelChangesMapComponent } from './components/fuel-changes-map/fuel-changes-map.component';
import { EmptyStateComponent } from "../../_shared/components/states/empty-state/empty-state.component";
import { BasePageComponent } from '../../_shared/components/page/base-page.component';
import { SearchComponent } from "../../_shared/components/filters/search/search.component";
import { CurrencyHelper } from '../../_shared/helpers/currency/currency-map.helper';
import { FuelChangeTypeFilter } from './enums/fuel-changes-type-filter.enum';
import { format } from 'date-fns';
import { buildDateFilterFromPageFilter } from '../../_shared/helpers/page-filter-state-helper';
import { DriverFilterComponent } from "../../_shared/components/filters/driver-filter/driver-filter.component";
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-changes',
  standalone: true,
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    CommonModule,
    FuelChangesMapComponent,
    DateFilterComponent,
    TotalFuelLossWidgetComponent,
    TotalFillUpsWidgetComponent,
    LossRatioWidgetComponent,
    FuelConsumptionWidgetComponent,
    AccountFilterComponent,
    PaginationComponent,
    EmptyStateComponent,
    SearchComponent,
    NgbDropdownModule,
    DriverFilterComponent
  ],
  templateUrl: './fuel-changes.component.html',
  styleUrl: './fuel-changes.component.scss'
})
export class FuelChangesComponent extends BasePageComponent {
  private readonly transloco = inject(TranslocoService);
  private modalService = inject(NgbModal);
  private filterService = inject(FilterService);
  private fuelChangesService = inject(FuelChangesService);
  private errorService = inject(ErrorHandlerService);
  private mapDataService = inject(CommonMapService);

  @Output() childResponse = new EventEmitter<{ type: string, options: any }>();
  isLoading: boolean = true;
  isLoadingLocations: boolean = true;
  isExporting: boolean = false;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  driverFilter: DriverFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  accountFilterChanged$ = new Subject<number[]>();
  showTableRowDetails: boolean = false;
  fuelChangesDetails: FuelChangesDetails[] = [];
  fuelChangesLocations: FuelChangesLocations[] = [];
  filteredData: FuelChangesLocations[] = [];
  expandedRow: string | null;
  page: number = 1;
  pageSize: number = 8;
  totalPages: number = 0;
  totalCount: number = 0;
  mapCenter: { lat: number, lng: number } = { lat: 0, lng: 0 };
  expandedRowRotated: { index: number, page: number } | null = null;
  searchTerm = '';
  currencyCode: string = '';
  currencySymbol: string = '';

  fuelChangeType: FuelChangeTypeFilter = FuelChangeTypeFilter.All;
  fuelChangeTypeFilter = FuelChangeTypeFilter;

  clusterRenderer: IClusterRenderer = new FuelChangesClusterRenderer();
  markerFactory: IMarkerFactory = new FuelChangesMarkerFactory();
  infoWindowBuilder: BaseMapInfoWindowBuilder = new FuelChangesMapInfoWindowBuilder(key => this.transloco.translate(key));
  filterLoaded: boolean = false;
  protected monthToDate = signal<boolean>(false);
  private cancelSubject: Subject<void> = new Subject<void>();
  private cancelSubjectLocations: Subject<void> = new Subject<void>();

  constructor() {
    super();
  }

  override ngOnInit(): void {
    this.mapDataService.updateData([]);
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.driverFilter = {} as DriverFilterData;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelSubject.next();
    this.cancelSubject.complete();

    this.cancelSubjectLocations.next();
    this.cancelSubjectLocations.complete();

    this.accountFilterChanged$.complete();
  }

  protected override getFilters(): any {
    return {
      ...this.dateFilter,
      ...this.accountFilter,
      ...this.driverFilter,
      searchTerm: this.searchTerm,
      fuelChangeType: this.fuelChangeType
    }
  }

  protected override applyPageFilterState(pageFilter: any): void {
    // Apply date filter
    this.dateFilter = buildDateFilterFromPageFilter(this.dateFilter, pageFilter);

    // Apply account filter
    this.accountFilter = { accountIds: pageFilter.accountIds } as AccountFilterData;

    // Apply other filters
    this.fuelChangeType = pageFilter.fuelChangeType;
    this.searchTerm = pageFilter.searchTerm;
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;

    // Emit account IDs for driver filter
    this.accountFilterChanged$.next(this.accountFilter.accountIds ?? []);

    this.updateFilter();
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.dateFilter = filter;
    this.updateFilter();
  }

  onDriverFilterChange(filter: DriverFilterData): void {
    this.page = 1;
    this.driverFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();
      this.getFuelChangesDetails();
      this.getFuelChangesLocations();
    }
  }

  formatCost(cost: number): string {
    return `${this.currencySymbol}${cost.toLocaleString()}`;
  }

  public async onSearchTermUpdate(searchTerm: string): Promise<void> {
    this.searchTerm = searchTerm;
    this.page = 1;

    this.updatePageFilterState();
    this.getFuelChangesDetails();
    this.getFuelChangesLocations();
  }

  public async onFuelChangeTypeFilter(fuelChangeType: FuelChangeTypeFilter): Promise<void> {
    this.fuelChangeType = fuelChangeType;
    this.page = 1;

    this.updatePageFilterState();
    this.getFuelChangesDetails();
    this.getFuelChangesLocations();
  }

  async getFuelChangesDetails(): Promise<void> {
    this.isLoading = true;
    try {
      const request: FuelChangesPaginationRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm,
        fuelChangeType: this.fuelChangeType,
        driverIds: this.driverFilter?.driverIds ?? []
      }

      this.cancelSubject.next();
      const response = await this.fuelChangesService.getFuelChangesDetails(request, this.cancelSubject);
      this.totalPages = response.data?.totalPages ?? 0;
      this.totalCount = response.data?.totalCount ?? 0;
      this.fuelChangesDetails = response.data?.results.map(item => {
        return {
          assetId: item.assetId,
          vehicle: item.assetName,
          driver: `${item.firstName} ${item.lastName}`,
          location: item.location,
          lat: item.lat,
          lng: item.lng,
          amount: item.amount,
          cost: item.cost,
          type: item.fuelStatusType,
          timestamp: item.timestamp,
          fuelCostDetailId: item.fuelCostDetailId,
          fuelLevelBeforeChange: item.fuelLevelBeforeChange,
          fuelLevelAfterChange: item.fuelLevelAfterChange,
          tank: item.tank,
          fuelConsumption: item.fuelConsumption,
          isValidFuelLevelBeforeChange: item.isValidFuelLevelBeforeChange,
          isValidFuelLevelAfterChange: item.isValidFuelLevelAfterChange
        };
      }) ?? [];
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  async getFuelChangesLocations(): Promise<void> {
    this.isLoadingLocations = true;

    try {
      const request: FuelChangesRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        searchTerm: this.searchTerm,
        fuelChangeType: this.fuelChangeType,
        driverIds: this.driverFilter?.driverIds ?? []
      }

      this.cancelSubjectLocations.next();
      const response = await this.fuelChangesService.getFuelChangesLocations(request, this.cancelSubjectLocations);
      this.fuelChangesLocations = response.data?.map(item => {
        return {
          assetId: item.assetId,
          vehicle: item.assetName,
          driver: `${item.firstName} ${item.lastName}`,
          amount: item.amount,
          cost: item.cost,
          type: item.fuelStatusType,
          lat: item.lat,
          lng: item.lng,
          location: item.location,
          timestamp: item.timestamp,
          tank: item.tank,
          fuelLevelBeforeChange: item.fuelLevelBeforeChange,
          fuelLevelAfterChange: item.fuelLevelAfterChange,
          distanceUntilEmpty: item.distanceUntilEmpty,
          isValidFuelLevel: item.isValidFuelLevel,
          currentFuelLevel: item.currentFuelLevel,
          isValidFuelLevelBeforeChange: item.isValidFuelLevelBeforeChange,
          isValidFuelLevelAfterChange: item.isValidFuelLevelAfterChange
        };
      }) ?? [];
      this.isLoadingLocations = false;
      this.mapDataService.updateData(this.fuelChangesLocations);
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingLocations = false;
        }
      });
    }
  }

  async exportFuelChangesExcel(): Promise<void> {
    this.isExporting = true;

    try {
      const request: FuelChangesRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        searchTerm: this.searchTerm,
        fuelChangeType: this.fuelChangeType,
        utcOffset: this.getCurrentUtcOffset(),
        driverIds: this.driverFilter?.driverIds ?? []
      };

      const blob = await this.fuelChangesService.exportFuelChangesDetailsExcel(request);

      if (blob.type === 'application/json') {
        const text = await blob.text();
        const errorResponse = JSON.parse(text);
        const errorMessage = errorResponse.message || 'Failed to export fuel changes';
        this.errorService.handleError({ error: { errorCode: errorResponse.errorCode, message: errorMessage } });
        this.isExporting = false;
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const defaultFilename = `FuelChangesDetails_${format(new Date(), 'yyyy-MM-dd HH-mm-ss')}.xlsx`;
      link.download = defaultFilename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.isExporting = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isExporting = false;
        }
      });
    } finally {
      this.isExporting = false;
    }
  }

  async exportFuelChangesCSV(): Promise<void> {
    this.isExporting = true;

    try {
      const request: FuelChangesRequest = {
        accountIds: this.accountFilter.accountIds,
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        searchTerm: this.searchTerm,
        fuelChangeType: this.fuelChangeType,
        utcOffset: this.getCurrentUtcOffset(),
        driverIds: this.driverFilter?.driverIds ?? []
      };

      const blob = await this.fuelChangesService.exportFuelChangesDetailsCSV(request);

      if (blob.type === 'application/json') {
        const text = await blob.text();
        const errorResponse = JSON.parse(text);
        const errorMessage = errorResponse.message || 'Failed to export fuel changes';
        this.errorService.handleError({ error: { errorCode: errorResponse.errorCode, message: errorMessage } });
        this.isExporting = false;
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const defaultFilename = `FuelChangesDetails_${format(new Date(), 'yyyy-MM-dd HH-mm-ss')}.csv`;
      link.download = defaultFilename;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.isExporting = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isExporting = false;
        }
      });
    } finally {
      this.isExporting = false;
    }
  }

  get showingCount(): number {
    if (this.totalCount === 0) return 0;

    const end = Math.min(this.page * this.pageSize, this.totalCount);
    return end;
  }

  get showLastRowBorder(): boolean {
    return !this.isLoading && !this.isLoadingLocations &&
      this.fuelChangesDetails?.length > 0 &&
      this.fuelChangesDetails.length < this.pageSize;
  }

  getRowKey(row: FuelChangesDetails, index: number): string {
    return `${row.assetId}-${row.timestamp}-${index}`;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.page = page;
    this.getFuelChangesDetails();
  }

  getFuelLevelDisplay(fuelLevel: number, isValid: boolean): string {
    return isValid !== false ? `${fuelLevel} ℓ` : 'N/A';
  }

  getFuelLevelBeforeDisplay(row: FuelChangesDetails): string {
    return this.getFuelLevelDisplay(row.fuelLevelBeforeChange, row.isValidFuelLevelBeforeChange);
  }

  getFuelLevelAfterDisplay(row: FuelChangesDetails): string {
    return this.getFuelLevelDisplay(row.fuelLevelAfterChange, row.isValidFuelLevelAfterChange);
  }

  openInvestigationModal(fuelChangeDetail: FuelChangesDetails): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    const modalRef = this.modalService.open(ValidationModal, modalOptions);
    modalRef.componentInstance.fuelChangeDetail = fuelChangeDetail;
  }

  getSelectedFilterLabel(filter: DateIntervalFilterData): string {
    return getSelectedFilterLabel(filter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  getModalFilterHeaders(filter: DateIntervalFilterData): { previous: string; current: string } {
    return getModalFilterHeaders(filter, (key, params) => this.transloco.translate(key, params), this.transloco.getActiveLang());
  }

  tableRowOpen(row: FuelChangesDetails, index: number): void {
    this.expandedRow = this.expandedRow === this.getRowKey(row, index) ? null : this.getRowKey(row, index);
    this.expandedRowRotated = this.shouldRotateExpandIcon(index, this.page) ? null : { index, page: this.page };
  }

  shouldRotateExpandIcon(index: number, page: number): boolean {
    return this.expandedRowRotated?.index === index && this.expandedRowRotated?.page === page;
  }

  onLocationClick(row: FuelChangesDetails, event: MouseEvent): void {
    event.stopPropagation();

    if (row.lat && row.lng) {
      this.mapCenter = { lat: row.lat, lng: row.lng };
    }
  }

  protected buttonMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected childResponseFuel(event: { type: string, options: any }): void {
    if (event.type === 'validation') {
      const modalRef = this.modalService.open(ValidationModal, event.options);
      modalRef.componentInstance.value = 100;
    }
  }

  protected onRefresh(): void {
    if (!this.isExporting) this.updateFilter();
  }

  protected override shouldRefresh(): boolean {
    return !isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter);
  }

  private getCurrentUtcOffset(): number {
    return -new Date().getTimezoneOffset() * 60;
  }
}